import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AuthService, User, EventProfile, AuthTokens } from '@shared/auth';
import { unifiedStorage } from '@/lib/unified-storage';

interface AuthContextType {
  user: User | null;
  eventProfile: EventProfile | null;
  eventId: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (tokens: AuthTokens) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Create a singleton instance of AuthService outside the component
const DOO_EVENTS_API = import.meta.env.VITE_DOO_EVENTS_API || 'http://localhost:3000';
const authServiceInstance = new AuthService(DOO_EVENTS_API);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [eventProfile, setEventProfile] = useState<EventProfile | null>(null);
  const [eventId, setEventId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Use the singleton instance
  const authService = authServiceInstance;

  useEffect(() => {
    // Add a small delay to ensure DOM is ready and avoid race conditions
    const timer = setTimeout(() => {
      initializeAuth();
    }, 100);
    
    return () => clearTimeout(timer);
  }, [searchParams]); // Re-run when URL params change

  const initializeAuth = async () => {
    console.log('[Queue Auth] Initializing authentication...');
    setIsLoading(true);
    
    try {
      // Check for token and eventId in URL params (coming from Doo Events)
      const urlToken = searchParams.get('token');
      const urlEventId = searchParams.get('eventId');
      
      console.log('[Queue Auth] URL params:', { 
        hasToken: !!urlToken, 
        eventId: urlEventId,
        tokenPreview: urlToken ? urlToken.substring(0, 20) + '...' : null 
      });
      
      
      // Determine the event ID from URL or UnifiedStorage
      let currentEventId = urlEventId || unifiedStorage.getEventId();
      
      // Always persist eventId when it comes from URL (highest priority)
      if (urlEventId) {
        console.log('[Queue Auth] Persisting eventId from URL:', urlEventId);
        unifiedStorage.setEventId(urlEventId);
        currentEventId = urlEventId;
      } else if (!currentEventId) {
        // No eventId in URL or storage, use default
        console.warn('[Queue Auth] No eventId found, using default');
        const defaultEventId = '68a38f9c6ac089df0b1c90f6'; // Default event ID
        unifiedStorage.setEventId(defaultEventId);
        currentEventId = defaultEventId;
      }
      
      // Always update state with the current event ID
      setEventId(currentEventId);
      console.log('[Queue Auth] Current eventId set to:', currentEventId);
      
      if (urlToken && urlEventId) {
        console.log('[Queue Auth] New authentication from Doo Events');
        
        // Store the token
        authService.setTokens({ accessToken: urlToken });
        
        // Clean URL for security - remove token but keep eventId for reference
        const newParams = new URLSearchParams();
        newParams.set('eventId', urlEventId); // Keep eventId in URL for consistency
        navigate({ search: newParams.toString() }, { replace: true });
      }
      
      // Sync tokens from unifiedStorage to authService (tokens live in queue_auth_tokens)
      const storedTokens = unifiedStorage.getTokens();
      if (storedTokens?.accessToken && !authService.isAuthenticated()) {
        console.log('[Queue Auth] Syncing tokens from unifiedStorage to authService');
        authService.setTokens(storedTokens);
      }

      // Try to get current user if authenticated
      if (authService.isAuthenticated()) {
        
        console.log('[Queue Auth] Authenticated, checking with eventId:', currentEventId);
        
        // EventId should already be set at this point, but double-check
        if (!currentEventId) {
          console.error('[Queue Auth] Critical: No eventId available after auth check');
          currentEventId = '68a38f9c6ac089df0b1c90f6'; // Fallback
        }
        
        try {
          console.log('[Queue Auth] Fetching current user...');
          const currentUser = await authService.getCurrentUser();
          
          if (!currentUser) {
            throw new Error('Failed to fetch user data');
          }
          
          console.log('[Queue Auth] User fetched:', currentUser);
          setUser(currentUser);
          
          // Fetch the real event profile to get the user's role in this event
          const userId = currentUser.id || (currentUser as any)._id || (currentUser as any).userId || 'unknown';
          try {
            console.log('[Queue Auth] Fetching event profile for eventId:', currentEventId, 'userId:', userId);
            const profileData = await authService.getEventProfile(currentEventId, userId);
            if (profileData) {
              console.log('[Queue Auth] Event profile fetched:', profileData);
              // Backend returns 'profile' field, map to 'role' for EventProfile interface
              setEventProfile({
                eventId: (profileData as any).eventId || currentEventId,
                userId: (profileData as any).userId || userId,
                role: (profileData as any).profile || (profileData as any).role || 'queue_user',
                permissions: (profileData as any).permissions || []
              });
            } else {
              console.log('[Queue Auth] No event profile returned, using default');
              setEventProfile({
                eventId: currentEventId,
                userId: userId,
                role: 'queue_user',
                permissions: []
              });
            }
          } catch (profileError) {
            console.warn('[Queue Auth] Could not fetch event profile, using default:', profileError);
            setEventProfile({
              eventId: currentEventId,
              userId: userId,
              role: 'queue_user',
              permissions: []
            });
          }
        } catch (error: any) {
          console.error('[Queue Auth] Failed to initialize auth:', error);
          console.error('[Queue Auth] Error details:', error.response?.data || error.message);
          
          // Only clear auth if it's a 401 (unauthorized) and we don't have a token
          if (error.response?.status === 401 && !authService.isAuthenticated()) {
            console.warn('[Queue Auth] 401 error and no auth, clearing state');
            authService.logout();
            localStorage.removeItem('current_event_id');
            setUser(null);
            setEventProfile(null);
            setEventId(null);
          } else {
            // For other errors or if we have a token, keep the auth state
            console.warn('[Queue Auth] Error but keeping auth state:', error.response?.status);
            // Set a default user to prevent redirect
            setUser({
              _id: 'temp-user',
              email: 'user@queue.com',
              name: 'Queue User',
              type: 'user',
              isActive: true
            } as any);
          }
        }
      }
    } catch (error) {
      console.error('Critical error during auth initialization:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (tokens: AuthTokens) => {
    authService.setTokens(tokens);
    
    try {
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);
      
      const urlEventId = searchParams.get('eventId');
      if (urlEventId) {
        // Save the event ID
        localStorage.setItem('current_event_id', urlEventId);
        setEventId(urlEventId);
        
        try {
          const profile = await authService.getEventProfile(urlEventId);
          setEventProfile(profile);
        } catch (profileError) {
          console.warn('Could not fetch event profile:', profileError);
        }
      }
    } catch (error) {
      console.error('Login failed:', error);
      authService.logout();
      throw error;
    }
  };

  const logout = () => {
    authService.logout();
    localStorage.removeItem('current_event_id');
    setUser(null);
    setEventProfile(null);
    setEventId(null);
    
    // Don't redirect - stay in the app
    console.log('[Queue Auth] Logout completed, staying in app');
  };

  const refreshUser = async () => {
    try {
      console.log('[Queue Auth] Refreshing user data...');
      const currentUser = await authService.getCurrentUser();
      if (currentUser) {
        setUser(currentUser);
        console.log('[Queue Auth] User data refreshed:', currentUser);
      }
    } catch (error) {
      console.error('[Queue Auth] Failed to refresh user:', error);
      throw error;
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      eventProfile,
      eventId,
      isLoading,
      isAuthenticated: authService.isAuthenticated(),
      login,
      logout,
      refreshUser
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}